import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Form,
  Modal,
  Select,
  Table,
  Input,
  Button,
  Space,
  notification,
  Tooltip,
  InputNumber,
  Typography,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import {
  PlusCircleTwoTone,
  MinusCircleOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  MenuOutlined,
  BackwardOutlined,
  StepBackwardOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";

import { Fetch, Post, Put, Delete, Patch } from "../../fetch";
import {
  convertIdToKey,
  convertIdtoSeriesName,
  removeUnpublished,
} from "../../utils";
import { Context } from "../../context";
import {
  validateInputFields,
  validateOptionalDescriptionFields,
} from "../../validators";
import EditPlaylists from "./EditPlaylists";
const { Option } = Select;
const { Column } = Table;
const { confirm } = Modal;
const { TextArea } = Input;
const Row = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      }
    ),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  };
  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if (child.key === "sort") {
          return React.cloneElement(child, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{
                  touchAction: "none",
                  cursor: "move",
                }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};
const ManagePlaylists = () => {
  const { apiUrl, token } = useContext(Context);

  const [page, setPage] = useState("home");
  const [playlists, setPlaylists] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [counter, setCounter] = useState(0);
  const [movies, setMovies] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [series, setSeries] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [songs, setSongs] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [artists, setArtists] = useState([]);
  const [playlistId, setPlaylistId] = useState();
  const [playlistType, setPlaylistType] = useState(null);
  const [disableAdd, setDisableAdd] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const { Title } = Typography;

  const [form] = Form.useForm();

  useEffect(() => {
    if (page) {
      Fetch(`${apiUrl}/playlists?page=${page}`, token, (result) => {
        const playlists = convertIdToKey(result.playlists).sort(
          (a, b) => a.position - b.position
        );
        if (playlists.length >= 5) {
          setDisableAdd(true);
        }
        setPlaylists(playlists);
      });
    }

    Fetch(`${apiUrl}/movies`, token, (result) => {
      const movies = removeUnpublished(convertIdToKey(result.movies));
      setMovies(movies);
    });

    Fetch(`${apiUrl}/episodes/all`, token, (result) => {
      const episodes = convertIdToKey(result.episodes);
      setEpisodes(episodes);
    });

    Fetch(`${apiUrl}/series`, token, (result) => {
      const series = convertIdToKey(result.series);
      Fetch(`${apiUrl}/seasons`, token, (result) => {
        if (result.success) {
          const seasons = removeUnpublished(
            convertIdtoSeriesName(series, result.seasons)
          );
          setSeasons(seasons);
        }
      });
      setSeries(series);
    });

    Fetch(`${apiUrl}/languages`, token, (result) => {
      const l = convertIdToKey(result.languages);
      setLanguages(l);
    });

    Fetch(`${apiUrl}/crew`, token, (result) => {
      const a = convertIdToKey(result.creators);
      setArtists(a);
    });
  }, [apiUrl, token, page, counter]);

  const handleSubmit = async () => {
    setConfirmLoading(true);
    try {
      const fields = await form.validateFields();
      console.log(fields);
      if (
        !["recommended", "languages", "artists", "top10"].includes(
          fields.playlistType
        )
      ) {
        const duplicates = fields.content.filter(
          (item, index) => fields.content.indexOf(item) !== index
        );
        if (duplicates.length > 0) {
          notification.error({
            message: "Manage Playlists Error",
            description: "Content has duplicates",
          });
          return;
        }
      }

      const fn = modalType === "Add" ? Post : Put;
      fn(
        modalType === "Add"
          ? `${apiUrl}/playlists`
          : `${apiUrl}/playlists/${playlistId}`,
        token,
        { ...fields, page },
        (result) => {
          if (result.success === true) {
            notification.success({
              message: "Success",
              description: "Playlists updated",
            });
            form.resetFields();
            setModalVisible(false);
            setConfirmLoading(false);
            setCounter(counter + 1);
          } else if (result.success === false) {
            notification.error({
              message: "Error",
              description: result["message"],
            });
            setConfirmLoading(false);
          }
        },
        () => {
          notification.error({
            message: "Manage Playlists Error",
            description: "Check console for more details",
          });
        }
      );
    } catch (err) {
    } finally {
      setConfirmLoading(false);
    }
  };

  const showEditModal = (record) => {
    setModalType("Edit");
    setPlaylistId(record.key);
    form.setFieldsValue(record);
    setPlaylistType(record.playlistType);
    setModalVisible(true);
  };

  const showConfirmDelete = (id) => {
    confirm({
      title: "Are you sure you want to delete this playlist?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        Delete(
          `${apiUrl}/playlists/${id}`,
          token,
          (result) => {
            if (result.success) {
              notification.success({
                message: "Success",
                description: "Playlist deleted successfully",
              });
              setCounter(counter + 1);
            }
          },
          () => {
            notification.error({
              message: "Error deleting playlist",
              description: "Please check console for more details",
            });
          }
        );
      },
    });
  };

  const handleSwap = (active, over) => {
    Patch(
      `${apiUrl}/playlists/swap`,
      token,
      {
        old: active,
        new: over,
      },
      (result) => {
        if (result.success) {
          setCounter(counter + 1);
        } else {
          notification.error({
            message: "Something Went Wrong",
            description: "Please refresh and try again",
          });
        }
      }
    );
  };

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setPlaylists((previous) => {
        const activeIndex = previous.findIndex((i) => i.key === active.id);
        const overIndex = previous.findIndex((i) => i.key === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
    }
    handleSwap(active.id, over.id);
  };

  const handleReset = () => {
    setPlaylistId(null);
    setPlaylistType(null);
    setShowAddEditModal(false);
    setCounter(counter + 1);
  };

  return (
    <div className="manage-episodes-wrapper">
      {showAddEditModal ? (
        <div>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              tooltip="Back to playlists"
              type="primary"
              onClick={handleReset}
              style={{ marginBottom: "10px" }}
            />
            <Title level={3}>Back to playlists</Title>
          </Space>
          <EditPlaylists
            id={playlistId}
            showModal={[showAddEditModal, setShowAddEditModal]}
            handleReset={handleReset}
          />
        </div>
      ) : (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={playlists.map((i) => i.key)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              components={{
                body: {
                  row: Row,
                },
              }}
              rowKey="key"
              dataSource={playlists}
              pagination={false}
            >
              <Column key={"sort"} />
              <Column
                title="Name"
                dataIndex="name"
                key="name"
                align="center"
                render={(_, record) => {
                  let title = record.name;
                  if (title !== undefined) {
                    if (title.length > 50) {
                      return (
                        <Tooltip title={title}>
                          <span>{`${title.slice(0, 50)}...`}</span>
                        </Tooltip>
                      );
                    } else {
                      return title;
                    }
                  } else {
                    return "-";
                  }
                }}
              />
              <Column
                title="Position"
                dataIndex="position"
                key="position"
                align="center"
              />
              <Column
                title="Actions"
                key="action"
                align="center"
                render={(text, record) => (
                  <Space size="middle">
                    <a
                      onClick={() => {
                        showEditModal(record);
                        setShowAddEditModal(true);
                      }}
                    >
                      Edit
                    </a>
                    {page !== "search" && (
                      <a onClick={() => showConfirmDelete(record.key)}>
                        Delete
                      </a>
                    )}
                  </Space>
                )}
              />
            </Table>
            <PlusCircleTwoTone
              twoToneColor="#1890FF"
              style={{ fontSize: "48px" }}
              className="add-series-btn"
              onClick={() => {
                // setModalVisible(true);
                setModalType("Add");
                form.resetFields();
                setShowAddEditModal(true);
              }}
            />
          </SortableContext>
        </DndContext>
      )}
      <Modal
        title={modalType + " Playlist"}
        open={false}
        onOk={handleSubmit}
        okText="Submit"
        onCancel={() => {
          setModalVisible(false);
          setPlaylistType(null);
        }}
        confirmLoading={confirmLoading}
        maskClosable={false}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name={"playlistType"}
            label={"Playlist Type"}
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Please select the Row"
              onChange={setPlaylistType}
            >
              <Option value="row_1">Row 1</Option>
              <Option value="row_2">Row 2</Option>
              <Option value="row_3">Row 3</Option>
              <Option value="row_4">Row 4</Option>
              <Option value="creators">Creators</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name={"position"}
            label="Position"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber min={1} placeholder="Enter Position" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                validator: validateInputFields,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={"description"}
            label="Description"
            rules={[
              {
                required: false,
                validator: validateOptionalDescriptionFields,
              },
            ]}
          >
            <TextArea showCount maxLength={1000} rows={10} />
          </Form.Item>
          {!["recommended", "top10"].includes(playlistType) && (
            <Form.List
              name="content"
              rules={[
                {
                  validator: async (_, content) => {
                    if (!content || content.length < 1) {
                      return Promise.reject(
                        new Error("At least 1 item required")
                      );
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map((field, index) => (
                    <Form.Item
                      label={index === 0 ? "Content" : ""}
                      required={false}
                      key={field.key}
                    >
                      <Form.Item
                        {...field}
                        validateTrigger={["onChange", "onBlur"]}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: "Please select item or remove this field",
                          },
                        ]}
                        noStyle
                      >
                        <Select
                          showSearch
                          className="form-card-selector"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          filterSort={(optionA, optionB) =>
                            optionA.children
                              .toLowerCase()
                              .localeCompare(optionB.children.toLowerCase())
                          }
                          style={{ width: "100%" }}
                        >
                          {playlistType === "row_1" && (
                            <>
                              {movies &&
                                movies.map((movie) => (
                                  <Option
                                    key={movie.key}
                                    value={`movie|${movie.key}`}
                                  >
                                    {movie.title}
                                  </Option>
                                ))}
                              {episodes &&
                                episodes.map((ep) => (
                                  <Option
                                    key={ep.key}
                                    value={`episode|${ep.key}`}
                                  >
                                    {ep.title}
                                  </Option>
                                ))}
                            </>
                          )}
                          {["row_2", "row_3", "row_4"].includes(
                            playlistType
                          ) && (
                            <>
                              {movies &&
                                movies.map((movie) => (
                                  <Option
                                    key={movie.key}
                                    value={`movie|${movie.key}`}
                                  >
                                    {movie.title}
                                  </Option>
                                ))}
                              {seasons &&
                                seasons.map((s) => (
                                  <Option key={s._id} value={`season|${s._id}`}>
                                    {s.enrichedName}
                                  </Option>
                                ))}
                            </>
                          )}
                          {playlistType === "creators" && (
                            <>
                              {artists &&
                                artists.map((a) => (
                                  <Option key={a.key} value={a.key}>
                                    {a.name}
                                  </Option>
                                ))}
                            </>
                          )}
                        </Select>
                      </Form.Item>
                      {fields.length > 1 ? (
                        <MinusCircleOutlined
                          className="dynamic-delete-button"
                          onClick={() => remove(field.name)}
                        />
                      ) : null}
                    </Form.Item>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      style={{ width: "60%" }}
                      icon={<PlusOutlined />}
                    >
                      Add item
                    </Button>
                    <Form.ErrorList errors={errors} />
                  </Form.Item>
                </>
              )}
            </Form.List>
          )}
        </Form>
      </Modal>
    </div>
  );
};
export default ManagePlaylists;
